import { Dayjs } from "dayjs";
import { Observable } from "rxjs";
import { inject, injectable } from "tsyringe";

import { RentalEndpoints } from "src/containers/rental/constants/endpoints";
import { autoBind } from "src/shared/decorators/autobind";
import { HttpClient } from "src/infra/http/client";
import { Environment } from "src/libs/environment";
import { replaceString } from "src/libs/replace-string";
import { PgaPolicy } from "src/shared/models/pga-policy.model";
import { MobileDocumentDownloadRequest } from "src/containers/documents-download/types";

declare global {
    interface Window {
        MobileDocumentDownload?: MessagePort;
    }
}

@injectable()
export class RentalRepository {
    private readonly BUSINESS_PROCESS_DOWNLOAD_RECEIPT = "OPTIONAL";

    constructor(@inject("HttpClient") private http: HttpClient) {}

    @autoBind
    downloadBallotMobile(
        userIdentity: string,
        policy: PgaPolicy,
        date: Dayjs
    ): void {
        const endpoint = new URL(
            replaceString(RentalEndpoints.BallotDownload, {
                ":policyId": policy.id,
            }),
            Environment.string("APIMGMT_URL")
        );
        endpoint.searchParams.append("system-code", policy.product.systemCode);
        endpoint.searchParams.append("year", date.year().toString());
        endpoint.searchParams.append("month", (date.month() + 1).toString());

        const payload: MobileDocumentDownloadRequest = {
            method: "GET",
            endpoint: endpoint.toString(),
            headers: this.getHeaders(userIdentity),
        };

        window.MobileDocumentDownload?.postMessage(JSON.stringify(payload));
    }

    @autoBind
    downloadBallotDesktop(
        userIdentity: string,
        policy: PgaPolicy,
        date: Dayjs
    ): Observable<object> {
        const url = replaceString(RentalEndpoints.BallotDownload, {
            ":policyId": policy.id,
        });
        const year = date.year();
        const month = date.month() + 1;

        return this.http.getJSON<object>(url, {
            host: Environment.string("APIMGMT_URL"),
            isPublicRequest: false,
            params: {
                "system-code": policy.product.systemCode,
                year,
                month,
            },
            customHeader: this.getHeaders(userIdentity),
        });
    }

    @autoBind
    private getHeaders(userIdentity: string) {
        return {
            ...this.http.httpUtils.getBaseHeaders(),
            ...this.http.httpUtils.getMsBaseHeaders(
                userIdentity,
                this.BUSINESS_PROCESS_DOWNLOAD_RECEIPT
            ),
        };
    }
}
